import type { ReactNode } from 'react';
import MuiCard from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';

type CardVariant = 'default' | 'outlined' | 'elevated' | 'glow';

interface CardProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  footer?: ReactNode;
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  sx?: object;
  onClick?: () => void;
  className?: string;
}

const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => prop !== 'cardVariant' && prop !== 'cardPadding' && prop !== 'clickable',
})<{ cardVariant?: CardVariant; cardPadding?: string; clickable?: boolean }>(
  ({ theme, cardVariant, clickable }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.surface.glassBorder}`,
    borderRadius: 16,
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',

    ...(clickable && {
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
        borderColor: alpha(theme.palette.venue.gaming, 0.2),
      },
      '&:active': {
        transform: 'translateY(-2px)',
      },
    }),

    ...(cardVariant === 'outlined' && {
      backgroundColor: 'transparent',
      border: `1.5px solid ${alpha(theme.palette.text.primary, 0.15)}`,
      boxShadow: 'none',
    }),

    ...(cardVariant === 'elevated' && {
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    }),

    ...(cardVariant === 'glow' && {
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
      borderColor: alpha(theme.palette.success.main, 0.2),
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: theme.palette.success.main,
      },
    }),
  })
);

const paddingMap = {
  none: 0,
  sm: 16,
  md: 24,
  lg: 32,
};

export function Card({
  children,
  title,
  subtitle,
  action,
  footer,
  variant = 'default',
  padding = 'md',
  sx,
  onClick,
  className,
}: CardProps) {
  const paddingValue = paddingMap[padding];

  return (
    <StyledCard
      cardVariant={variant}
      clickable={!!onClick}
      onClick={onClick}
      sx={sx}
      className={className}
    >
      {(title || action) && (
        <CardHeader
          title={title}
          subheader={subtitle}
          action={action}
          sx={{
            pb: 0,
            '& .MuiCardHeader-title': {
              fontFamily: '"IBM Plex Mono", monospace',
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary',
            },
            '& .MuiCardHeader-subheader': {
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontFamily: '"IBM Plex Sans", sans-serif',
            },
          }}
        />
      )}
      <CardContent
        sx={{
          padding: padding === 'none' ? 0 : `${paddingValue}px`,
          '&:last-child': {
            paddingBottom: padding === 'none' ? 0 : `${paddingValue}px`,
          },
        }}
      >
        {children}
      </CardContent>
      {footer && (
        <CardActions
          sx={{
            padding: `0 ${paddingValue}px ${paddingValue}px`,
            borderTop: (theme) => `1px solid ${theme.palette.surface.glassBorder}`,
            pt: 2,
            mt: 1,
          }}
        >
          {footer}
        </CardActions>
      )}
    </StyledCard>
  );
}

// Stat card variant for dashboards
interface StatCardProps {
  label: string;
  value: string | number;
  icon?: ReactNode;
  trend?: {
    value: number;
    label: string;
  };
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning';
  isLoading?: boolean;
}

export function StatCard({ label, value, icon, trend, color = 'primary', isLoading = false }: StatCardProps) {
  return (
    <Card variant="glow">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          {isLoading ? (
            <>
              <Box
                sx={{
                  height: 12,
                  width: '60%',
                  backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.06),
                  borderRadius: 1,
                  mb: 1,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.5,
                    },
                  },
                }}
              />
              <Box
                sx={{
                  height: 36,
                  width: '80%',
                  backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                  borderRadius: 1,
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 1,
                    },
                    '50%': {
                      opacity: 0.5,
                    },
                  },
                }}
              />
            </>
          ) : (
            <>
              <Box
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: '"IBM Plex Mono", monospace',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  mb: 1,
                }}
              >
                {label}
              </Box>
              <Box
                sx={{
                  fontSize: '2.25rem',
                  fontWeight: 600,
                  fontFamily: '"IBM Plex Mono", monospace',
                  color: 'text.primary',
                  lineHeight: 1,
                }}
              >
                {value}
              </Box>
              {trend && (
                <Box
                  sx={{
                    mt: 1.5,
                    fontSize: '0.8125rem',
                    fontFamily: '"IBM Plex Sans", sans-serif',
                    color: trend.value >= 0 ? 'success.main' : 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 500,
                  }}
                >
                  {trend.value >= 0 ? '↑' : '↓'} {Math.abs(trend.value)}% {trend.label}
                </Box>
              )}
            </>
          )}
        </Box>
        {icon && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: (theme) => alpha(theme.palette[color === 'primary' ? 'success' : color].main, isLoading ? 0.04 : 0.08),
              color: (theme) => alpha(theme.palette[color === 'primary' ? 'success' : color].main, isLoading ? 0.3 : 1),
              '& svg': {
                fontSize: '1.5rem',
              },
            }}
          >
            {icon}
          </Box>
        )}
      </Box>
    </Card>
  );
}
